import { useState, useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import { useAuthStore } from './store/useAuthStore'
import { I18nProvider } from './i18n'
import { LoadingScreen } from './components/LoadingScreen'
import { HomePage } from './pages/HomePage'
import { LoginPage } from './pages/LoginPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { LogoutPage } from './pages/LogoutPage'
import { AccountPage } from './pages/AccountPage'
import { SongListPage } from './pages/SongListPage'
import { SongEditorPage } from './pages/SongEditorPage'
import { ShareViewPage } from './pages/ShareViewPage'
import { OfflineSongsPage } from './pages/OfflineSongsPage'
import { PlaylistPage } from './pages/PlaylistPage'
import { PlaylistLivePage } from './pages/PlaylistLivePage'
import { useOnlineStatus } from './lib/offline/useOnlineStatus'
import { syncPending } from './lib/offline/syncQueue'
import { OFFLINE_ENABLED, isForceOffline } from './config/offline'

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage initialTab="login" />} />
      <Route path="/register" element={<LoginPage initialTab="register" />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/songs" element={<SongListPage />} />
      <Route path="/songs/collection/:collectionId" element={<SongListPage />} />
      <Route path="/songs/:id" element={<SongEditorPage />} />
      <Route path="/share/:token" element={<ShareViewPage />} />
      <Route path="/share/song/:token" element={<ShareViewPage mode="song" />} />
      <Route path="/playlist/:playlistId" element={<PlaylistPage />} />
      <Route path="/playlist/:playlistId/live" element={<PlaylistLivePage />} />
      <Route path="/offline" element={<OfflineSongsPage />} />
    </Routes>
  )
}

export function App() {
  const initialize = useAuthStore((s) => s.initialize)
  const [splashDone, setSplashDone] = useState(false)
  const [authReady, setAuthReady] = useState(false)

  // Start auth init immediately (parallel to splash screen).
  // Hard timeout after 5s — we never want to hang on a slow/stale session.
  useEffect(() => {
    let done = false

    const timeout = setTimeout(() => {
      if (!done) {
        console.warn('Auth init timeout — continuing anyway')
        setAuthReady(true)
      }
    }, 5000)

    initialize().finally(() => {
      done = true
      clearTimeout(timeout)
      setAuthReady(true)
    })

    return () => clearTimeout(timeout)
  }, [initialize])

  const ready = splashDone && authReady

  if (!ready) {
    return <LoadingScreen onDone={() => setSplashDone(true)} />
  }

  return (
    <I18nProvider>
      <SyncRunner />
      <AppRoutes />
    </I18nProvider>
  )
}

// Watches the network status and flushes any pending offline writes
// to Supabase as soon as we're back online (or merely "slow"). The
// sync queue itself is re-entrancy safe.
function SyncRunner() {
  const status = useOnlineStatus()
  useEffect(() => {
    if (!OFFLINE_ENABLED) return
    if (status === 'offline') return
    if (isForceOffline()) return
    void syncPending()
  }, [status])
  return null
}
